import { Trans } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";

import {
  ContinuousQuestion,
  DichotomousQuestion,
  MultiChoiceQuestion,
  OrdinalQuestion,
  OrdinalQuestionOption,
  Questionnaire,
  SingleChoiceQuestion,
} from "../../../api";
import { Checkboxes } from "../../../components/ui-kit/fields/checkboxes";
import { NumberInput } from "../../../components/ui-kit/fields/number-input";
import { Radio } from "../../../components/ui-kit/fields/radio";
import { RequiredAsterisk } from "../../../components/ui-kit/required-asterisk";
import { QuestionResponse } from "./questionnaire-model";

export type QuestionDto = Questionnaire["questions"][0];

export type QuestionType = QuestionDto["question_type"];

export const Question = observer(function Question(props: {
  question: QuestionDto;
  response: QuestionResponse;
}) {
  return (
    <div>
      <div>
        <b>{props.question.question_text}</b>
        {props.question.required && (
          <>
            {" "}
            <RequiredAsterisk />
          </>
        )}
      </div>

      <div className="h-1" />

      {(props.question.question_type === "dichotomous" ||
        props.question.question_type === "polytomous_single_choice" ||
        props.question.question_type === "ordinal") && (
        <SingleChoiceInput
          question={
            props.question as
              | DichotomousQuestion
              | SingleChoiceQuestion
              | OrdinalQuestion
          }
          response={props.response}
        />
      )}

      {props.question.question_type === "polytomous_multi_choice" && (
        <MultiChoiceInput
          question={props.question as MultiChoiceQuestion}
          response={props.response}
        />
      )}

      {props.question.question_type === "continuous" && (
        <ContinuousInput
          question={props.question as ContinuousQuestion}
          response={props.response}
        />
      )}
    </div>
  );
});

const SingleChoiceInput = observer(function SingleChoiceInput(props: {
  question: DichotomousQuestion | SingleChoiceQuestion | OrdinalQuestion;
  response: QuestionResponse;
}) {
  return (
    <div className="flex flex-col">
      <Radio
        values={props.question.question_options.reduce(
          (acc, cur) => {
            const key =
              props.question.question_type === "ordinal"
                ? (cur as OrdinalQuestionOption).option_value
                : cur.option_id;

            acc[key] = cur.option_text ?? "";

            return acc;
          },
          {} as Record<string, string>,
        )}
        model={props.response}
        attr="response"
        required={props.question.required}
        reverse
      />
    </div>
  );
});

const MultiChoiceInput = observer(function MultiChoiceInput(props: {
  question: MultiChoiceQuestion;
  response: QuestionResponse;
}) {
  return (
    <div className="flex flex-col">
      <Checkboxes
        values={props.question.question_options.reduce(
          (acc, cur) => {
            acc[cur.option_id] = cur.option_text ?? "";
            return acc;
          },
          {} as Record<string, string>,
        )}
        model={props.response}
        attr="response"
        min={props.question.ge_selected}
        max={props.question.le_selected}
      />

      <div className="h-1" />

      {props.question.ge_selected != null && (
        <span>
          <Trans>Check at least {props.question.ge_selected} options.</Trans>
        </span>
      )}
      {props.question.le_selected != null && (
        <span>
          <Trans>Check at most {props.question.le_selected} options.</Trans>
        </span>
      )}
    </div>
  );
});

const ContinuousInput = observer(function ContinuousInput(props: {
  question: ContinuousQuestion;
  response: QuestionResponse;
}) {
  return (
    <div className="flex flex-col">
      <div>
        <NumberInput
          model={props.response}
          attr="response"
          required={props.question.required}
        />
      </div>

      <div className="h-1" />

      {props.question.value_ge != null && (
        <span>
          <Trans>Must be at least {props.question.value_ge}.</Trans>
        </span>
      )}
      {props.question.value_gt != null && (
        <span>
          <Trans>Must be greater than {props.question.value_gt}.</Trans>
        </span>
      )}
      {props.question.value_le != null && (
        <span>
          <Trans>Must be at most {props.question.value_le}.</Trans>
        </span>
      )}
      {props.question.value_lt != null && (
        <span>
          <Trans>Must be lesser than {props.question.value_lt}.</Trans>
        </span>
      )}
      {props.question.value_multiple_of != null && (
        <span>
          <Trans>
            Must be a multiple of {props.question.value_multiple_of}.
          </Trans>
        </span>
      )}
    </div>
  );
});
